import React from 'react';
import {
  Card,
  Space,
  Typography,
  Divider,
  Row,
  Col,
  Tag,
  Tooltip
} from 'antd';
import {
  InfoCircleOutlined,
  CalendarOutlined,
  DollarOutlined
} from '@ant-design/icons';

const { Text, Title } = Typography;

interface PricingData {
  monthlyPricePerFacility: number;
  annualPricePerFacility: number;
  setupFeePerFacility: number;
}

interface CostBreakdownProps {
  activeFacilities: number;
  billingCycle: 'Monthly' | 'Annual';
  isTrialActive?: boolean;
  trialEndsAt?: string;
  pricingData: PricingData;
}

export const CostBreakdown: React.FC<CostBreakdownProps> = ({
  activeFacilities,
  billingCycle,
  isTrialActive = false,
  trialEndsAt,
  pricingData
}) => {
  const isAnnual = billingCycle === 'Annual';
  const pricePerFacility = isAnnual ? pricingData.annualPricePerFacility : pricingData.monthlyPricePerFacility;
  const subscriptionTotal = activeFacilities * pricePerFacility;
  const setupFeeTotal = activeFacilities * pricingData.setupFeePerFacility;
  
  // Calculate annual savings
  const monthlyCostPerYear = activeFacilities * pricingData.monthlyPricePerFacility * 12;
  const annualSavings = monthlyCostPerYear - (activeFacilities * pricingData.annualPricePerFacility);
  const savingsPercentage = Math.round((annualSavings / monthlyCostPerYear) * 100);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Card 
      size="small" 
      style={{ 
        background: isTrialActive ? '#f6ffed' : '#fafafa',
        border: isTrialActive ? '1px solid #b7eb8f' : '1px solid #d9d9d9'
      }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        {/* Header */}
        <div style={{ textAlign: 'center' }}>
          <Title level={5} style={{ margin: 0, color: isTrialActive ? '#52c41a' : '#1890ff' }}>
            {isTrialActive ? (
              <>
                <CalendarOutlined /> Free Trial Active
              </>
            ) : (
              <>
                <DollarOutlined /> Current Subscription
              </>
            )}
          </Title>
          {isTrialActive && trialEndsAt && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Billing starts: {trialEndsAt}
            </Text>
          )}
        </div>

        <Divider style={{ margin: '8px 0' }} />

        {/* Subscription Cost */}
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Text strong>
                {activeFacilities} Facilit{activeFacilities !== 1 ? 'ies' : 'y'} - {billingCycle}
              </Text>
              <Tooltip title={`${formatCurrency(pricePerFacility)} per facility per ${billingCycle.toLowerCase()}`}>
                <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
              </Tooltip>
            </Space>
          </Col>
          <Col>
            <Text strong style={{ fontSize: '16px' }}>
              {formatCurrency(subscriptionTotal)}
            </Text>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
              {isAnnual ? `/year` : `/month`}
            </Text>
          </Col>
        </Row>

        {/* Annual Savings */}
        {isAnnual && annualSavings > 0 && (
          <Row justify="space-between" align="middle">
            <Col>
              <Space>
                <Text type="success">Annual Savings</Text>
                <Tag color="green">{savingsPercentage}% off</Tag>
              </Space>
            </Col>
            <Col>
              <Text type="success" strong>
                -{formatCurrency(annualSavings)}
              </Text>
            </Col>
          </Row>
        )}

        {/* Setup Fees */}
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Text type="secondary">Setup Fees (one-time)</Text>
              <Tooltip title={`${formatCurrency(pricingData.setupFeePerFacility)} per facility, charged ${isTrialActive ? 'when trial ends' : 'for new facilities'}`}>
                <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
              </Tooltip>
            </Space>
          </Col>
          <Col>
            <Text type="secondary">
              {formatCurrency(setupFeeTotal)}
            </Text>
          </Col>
        </Row>

        {/* Per-facility breakdown */}
        <Divider style={{ margin: '8px 0' }} />
        <div style={{ background: '#f5f5f5', padding: '8px', borderRadius: '4px' }}>
          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
            Per Facility Pricing:
          </Text>
          <Row justify="space-between">
            <Col>
              <Text style={{ fontSize: '12px' }}>
                {billingCycle}: {formatCurrency(pricePerFacility)}
              </Text>
            </Col>
            <Col>
              <Text style={{ fontSize: '12px' }}>
                Setup: {formatCurrency(pricingData.setupFeePerFacility)}
              </Text>
            </Col>
          </Row>
          {isAnnual && (
            <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
              (Monthly equivalent: {formatCurrency(pricingData.annualPricePerFacility / 12)}/facility/month)
            </Text>
          )}
        </div>
      </Space>
    </Card>
  );
}; 